import { DashboardLayout } from '@/layouts/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Users, Briefcase, Clock, TrendingUp, Eye, CheckCircle2 } from 'lucide-react';

export default function RecruiterDashboard() {
  const stats = [
    { label: 'Active Jobs', value: '12', icon: Briefcase, trend: '+2 this week' },
    { label: 'New Applications', value: '34', icon: Users, trend: '+8 today' },
    { label: 'Interviews Scheduled', value: '15', icon: Clock, trend: '5 this week' },
    { label: 'Candidates Shortlisted', value: '28', icon: CheckCircle2, trend: '+6 pending' },
  ];

  const recentApplications = [
    {
      id: '1',
      candidate: 'Sarah Johnson',
      job: 'Senior Software Engineer',
      appliedDate: '2 hours ago',
      aiScore: 94,
      status: 'new',
      skills: ['React', 'Node.js', 'AWS'],
      experience: '8 years',
    },
    {
      id: '2',
      candidate: 'Michael Chen',
      job: 'Product Manager',
      appliedDate: '5 hours ago',
      aiScore: 89,
      status: 'new',
      skills: ['Agile', 'Strategy', 'Data Analysis'],
      experience: '6 years',
    },
    {
      id: '3',
      candidate: 'Emily Davis',
      job: 'UX Designer',
      appliedDate: '1 day ago',
      aiScore: 91,
      status: 'reviewed',
      skills: ['Figma', 'User Research', 'Prototyping'],
      experience: '5 years',
    },
  ];

  const activeJobs = [
    { id: '1', title: 'Senior Software Engineer', applications: 45, shortlisted: 8, interviews: 3 },
    { id: '2', title: 'Product Manager', applications: 32, shortlisted: 6, interviews: 2 },
    { id: '3', title: 'UX Designer', applications: 28, shortlisted: 5, interviews: 1 },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Recruiter Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage candidates and track hiring progress</p>
        </div>

        {/* Stats */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <div className="flex items-center justify-between mb-4">
                <div className="rounded-lg bg-primary-100 p-3 text-primary-600">
                  <stat.icon className="h-6 w-6" />
                </div>
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <p className="text-sm text-gray-600">{stat.label}</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.trend}</p>
            </Card>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* New Applications */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Recent Applications</CardTitle>
                  <div className="flex space-x-2">
                    <Button size="sm" variant="outline">Filter</Button>
                    <Button size="sm" variant="ghost">View All</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentApplications.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white font-medium">
                            {app.candidate.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{app.candidate}</h4>
                            <p className="text-sm text-gray-600">{app.job}</p>
                            <p className="text-xs text-gray-500">{app.experience} experience</p>
                          </div>
                        </div>
                        <Badge variant={app.status === 'new' ? 'warning' : 'info'}>
                          {app.status === 'new' ? 'New' : 'Reviewed'}
                        </Badge>
                      </div>
                      
                      <div className="bg-gradient-to-r from-primary-50 to-secondary-50 rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-700 flex items-center">
                            <Eye className="h-4 w-4 mr-1 text-primary-600" />
                            AI Match Score
                          </span>
                          <span className="text-xl font-bold text-primary-600">{app.aiScore}%</span>
                        </div>
                        <div className="w-full bg-white/50 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-primary-600 to-secondary-600 h-2 rounded-full" 
                            style={{ width: `${app.aiScore}%` }}
                          />
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        {app.skills.map((skill) => (
                          <span key={skill} className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                            {skill}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Applied {app.appliedDate}</span>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline">View Profile</Button>
                          <Button size="sm">Shortlist</Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Active Jobs Pipeline */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Active Jobs Pipeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeJobs.map((job) => (
                    <div key={job.id} className="border border-gray-200 rounded-lg p-4">
                      <h5 className="font-semibold text-gray-900 mb-3">{job.title}</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Applications</span>
                          <span className="font-medium text-gray-900">{job.applications}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Shortlisted</span>
                          <span className="font-medium text-primary-600">{job.shortlisted}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Interviews</span>
                          <span className="font-medium text-green-600">{job.interviews}</span>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="w-full mt-3">
                        View Pipeline
                      </Button>
                    </div>
                  ))}
                </div>
                <Button variant="ghost" className="w-full mt-4">
                  <Briefcase className="h-4 w-4 mr-2" />
                  Create New Job
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI Insights */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Eye className="h-5 w-5 mr-2 text-primary-600" />
              AI Insights & Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h6 className="font-semibold text-blue-900 mb-2">Top Candidate This Week</h6>
                <p className="text-sm text-blue-800">Sarah Johnson scored 94% for Senior Software Engineer. Strong technical skills match.</p>
                <Button size="sm" variant="outline" className="mt-3">Review Now</Button>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h6 className="font-semibold text-green-900 mb-2">Bias Check Passed</h6>
                <p className="text-sm text-green-800">All hiring decisions this week passed bias audits. No disparate impact detected.</p>
                <Button size="sm" variant="outline" className="mt-3">View Report</Button>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h6 className="font-semibold text-yellow-900 mb-2">Action Needed</h6>
                <p className="text-sm text-yellow-800">5 candidates awaiting review for more than 48 hours. Respond to maintain good candidate experience.</p>
                <Button size="sm" variant="outline" className="mt-3">Review Pending</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
